import StoreDevice from '../../../redux/models/Device'
import StoreDeviceCoordinate from '../../../redux/models/DeviceCoordinate'

const MAX_COORDINATE_HISTORY = 250

const uuidKey = u => String(u ?? '').trim().toLowerCase()

/** Compare two id strings: case-insensitive, optional hyphen-insensitive for GUID-like values. */
export const uuidMatches = (targetRaw, candidateRaw) => {
  const t = uuidKey(targetRaw)
  const c = uuidKey(candidateRaw)
  if (!t || !c) return false
  if (t === c) return true
  const tc = t.replace(/-/g, '')
  const cc = c.replace(/-/g, '')
  if (tc.length >= 8 && tc === cc) return true
  return false
}

const candidateUuidsFromDeviceRow = (d, storeKey) => {
  const out = []
  const push = v => {
    if (v != null && v !== '') out.push(v)
  }
  push(storeKey)
  if (d && typeof d === 'object') {
    push(d.id)
    push(d.uuidDevice)
    push(d.uuid)
    push(d.uuid_device)
    push(d.deviceId)
    push(d.device_id)
    push(d.trackerId)
    push(d.tracker_id)
    push(d.trackerUuid)
    push(d.tracker_uuid)
    push(d.hardwareId)
    push(d.hardware_id)
    push(d.imei)
  }
  return out
}

/**
 * Server may emit the payload object, or a tuple like ["coordinates created", { ... }].
 */
export const parseCoordinatesCreatedPayload = raw => {
  if (raw == null) return null
  if (Array.isArray(raw) && raw.length >= 2 && typeof raw[0] === 'string') {
    return typeof raw[1] === 'object' && raw[1] !== null ? raw[1] : null
  }
  if (typeof raw === 'object') return raw
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parseCoordinatesCreatedPayload(parsed)
    } catch {
      return null
    }
  }
  return null
}

/**
 * Match socket hardware / logical id to a row in `state.data.devices`.
 * Covers phones (uuidDevice, …) and trackers (uuid, tracker ids, snake_case API fields).
 */
export const findDeviceIdForCoordinateUuid = (devicesById, uuidDevice) => {
  const target = uuidKey(uuidDevice)
  if (!target) return null
  if (!devicesById || typeof devicesById !== 'object') return null

  for (const key of Object.keys(devicesById)) {
    const d = devicesById[key]
    if (!d) continue
    const canonicalId = String(d.id ?? key)
    for (const cand of candidateUuidsFromDeviceRow(d, key)) {
      if (uuidMatches(target, cand)) return canonicalId
    }
  }
  return null
}

const parseEventDate = payload => {
  if (payload.ondate_coordinate) {
    const d = new Date(payload.ondate_coordinate)
    if (!Number.isNaN(d.getTime())) return d
  }
  if (payload.locationTimeStamp) {
    const d = new Date(String(payload.locationTimeStamp).replace(' ', 'T'))
    if (!Number.isNaN(d.getTime())) return d
  }
  return new Date()
}

const deviceRowToPlain = row => {
  if (!row) return null
  if (typeof row.toJSON === 'function') return row.toJSON()
  return { ...row }
}

/**
 * Prepends a coordinate id to the device's `coordinateIds` (map uses `coordinates[0]` as current position).
 */
export const buildDeviceWithPrependedCoordinateId = (devicesById, deviceId, coordId) => {
  if (!deviceId || !coordId) return null
  const existingRow = devicesById[deviceId]
  if (!existingRow) return null
  const plain = deviceRowToPlain(existingRow)
  const prevIds = Array.isArray(plain.coordinateIds) ? plain.coordinateIds : []
  const nextIds = [coordId, ...prevIds.filter(cid => cid !== coordId)].slice(0, MAX_COORDINATE_HISTORY)
  return StoreDevice.FromJSON({
    ...plain,
    id: deviceId,
    coordinateIds: nextIds
  })
}

/**
 * @returns {{ coordId: string, storeCoord: StoreDeviceCoordinate, deviceId: string, storeDevice: StoreDevice } | null}
 */
export const buildCoordinateCreatedMerge = (devicesById, payload) => {
  const uuid = payload.uuid_device ??
    payload.uuidDevice ??
    payload.deviceId ??
    payload.device_id ??
    payload.uuid_tracker ??
    payload.trackerUuid ??
    payload.tracker_uuid ??
    payload.trackerId ??
    payload.tracker_id
  const deviceId = findDeviceIdForCoordinateUuid(devicesById, uuid)
  if (!deviceId || payload.lat == null || payload.lng == null) return null

  const existingRow = devicesById[deviceId]
  if (!existingRow) return null

  const coordId = `rt:${deviceId}:${Date.now()}:${Math.random().toString(36).slice(2, 11)}`
  const eventDate = parseEventDate(payload)

  const storeCoord = StoreDeviceCoordinate.FromNormalizedRESTModel({
    id: coordId,
    date: eventDate.toISOString(),
    lat: Number(payload.lat),
    lng: Number(payload.lng),
    speed: payload.speed != null ? Number(payload.speed) : 0,
    direction: payload.course != null ? Number(payload.course) : 0,
    horizontalAccuracy: payload.horizontalAccuracy != null
      ? Number(payload.horizontalAccuracy)
      : undefined,
    altitude: payload.altitude != null
      ? Number(payload.altitude)
      : (payload.alt != null ? Number(payload.alt) : undefined),
    verticalAccuracy: payload.verticalAccuracy != null
      ? Number(payload.verticalAccuracy)
      : (payload.vac != null ? Number(payload.vac) : undefined),
    floor: payload.floor != null ? Number(payload.floor) : undefined,
    locationString: undefined,
    deviceId
  })

  const storeDevice = buildDeviceWithPrependedCoordinateId(devicesById, deviceId, coordId)
  if (!storeDevice) return null

  return { coordId, storeCoord, deviceId, storeDevice }
}
